import { create } from 'zustand';

type SuppressEntry = {
  /** Cue current itemId at click time (hide until Current advances past this). */
  fromItemId?: string;
  /** Take target itemId when known (`playMedia` / seeded `nextMedia`) — for immediate agent spinner. */
  toItemId?: string;
  /**
   * Click-time agent → agent (both from/to are media-agent decks). Drives the
   * "Switching deck on media engine" spinner without waiting on cue WS.
   */
  mediaEngineDeckSwitch?: boolean;
  suppressedAtMs: number;
  /** True after we observed presentationTransitionInFlight for this advance. */
  sawTransitionInFlight?: boolean;
};

type LiveSlideChromeSuppressState = {
  byEntityRef: Record<string, SuppressEntry>;
  /** Call on nextMedia / prevMedia / playMedia — Current slide chrome hides immediately. */
  suppressForCueAdvance: (
    entityRef: string,
    fromItemId?: string,
    toItemId?: string,
    mediaEngineDeckSwitch?: boolean,
  ) => void;
  clear: (entityRef: string) => void;
  isSuppressed: (entityRef: string) => boolean;
  /**
   * Clear once Current has moved off the pre-click item and the presentation shield is down.
   * Until a shield has been observed (or a short grace elapses), keep suppressed so insert→resume
   * cannot flash the outgoing LiveKit deck when cue identity flips before hide WS arrives.
   */
  syncClearAfterAdvance: (
    entityRef: string,
    params: {
      currentItemId?: string;
      presentationTransitionInFlight?: boolean;
      /**
       * Agent → agent (or take onto an agent deck): wait until the presentation shield is down.
       * Non-agent takes (e.g. PPT → session info) must clear as soon as Current leaves fromItem.
       * Default true so existing agent callers keep shield timing.
       */
      waitForPresentationShield?: boolean;
      /**
       * Operator snapshot already matches the on-air commit players are showing.
       * Clear without waiting for the inFlight saw/clear cycle.
       */
      onAirPaintReady?: boolean;
    },
  ) => void;
};

const SAFETY_CLEAR_MS = 15_000;

export const useLiveSlideChromeSuppressStore = create<LiveSlideChromeSuppressState>((set, get) => ({
  byEntityRef: {},
  suppressForCueAdvance: (entityRef, fromItemId, toItemId, mediaEngineDeckSwitch) => {
    if (!entityRef) {
      return;
    }
    set((state) => ({
      byEntityRef: {
        ...state.byEntityRef,
        [entityRef]: {
          fromItemId,
          toItemId,
          mediaEngineDeckSwitch: mediaEngineDeckSwitch === true,
          suppressedAtMs: Date.now(),
          sawTransitionInFlight: false,
        },
      },
    }));
  },
  clear: (entityRef) => {
    if (!entityRef || !get().byEntityRef[entityRef]) {
      return;
    }
    set((state) => {
      const next = { ...state.byEntityRef };
      delete next[entityRef];
      return { byEntityRef: next };
    });
  },
  isSuppressed: (entityRef) => {
    if (!entityRef) {
      return false;
    }
    const entry = get().byEntityRef[entityRef];
    if (!entry) {
      return false;
    }
    if (Date.now() - entry.suppressedAtMs >= SAFETY_CLEAR_MS) {
      get().clear(entityRef);
      return false;
    }
    return true;
  },
  syncClearAfterAdvance: (entityRef, params) => {
    const entry = get().byEntityRef[entityRef];
    if (!entry) {
      return;
    }

    if (Date.now() - entry.suppressedAtMs >= SAFETY_CLEAR_MS) {
      get().clear(entityRef);
      return;
    }

    const waitForShield = params.waitForPresentationShield !== false;
    if (params.presentationTransitionInFlight === true && !entry.sawTransitionInFlight) {
      set((state) => {
        const prev = state.byEntityRef[entityRef];
        if (!prev) {
          return state;
        }
        return {
          byEntityRef: {
            ...state.byEntityRef,
            [entityRef]: { ...prev, sawTransitionInFlight: true },
          },
        };
      });
    }

    const latest = get().byEntityRef[entityRef];
    if (!latest) {
      return;
    }

    // Take target already matches on-air (players unhid). Insert may keep auto currentStep on the
    // outgoing row — do not wait for cue identity or a stuck inFlight flag.
    if (params.onAirPaintReady === true) {
      get().clear(entityRef);
      return;
    }

    if (params.presentationTransitionInFlight === true && waitForShield) {
      return;
    }

    if (latest.sawTransitionInFlight && params.presentationTransitionInFlight === false) {
      get().clear(entityRef);
      return;
    }

    const current = params.currentItemId;
    if (!current) {
      return;
    }
    if (latest.fromItemId && current === latest.fromItemId) {
      return;
    }

    // Non-agent take: Current has left the outgoing item — show Now immediately.
    if (!waitForShield) {
      get().clear(entityRef);
      return;
    }
    // Agent take without an inFlight WS yet: keep chrome until paint-ready, saw+clear, or 15s.
  },
}));
